import React from "react";
import { FormProvider, useForm } from "react-hook-form";
import { Link, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { Button, Input } from "component-library";
import {
  ArrowLeftIcon,
  ArrowPathIcon,
  ArrowTopRightOnSquareIcon,
} from "@heroicons/react/16/solid";
import { PraxisConfig } from "@shared/praxis-config";
import { PraxisDetails } from "@shared/praxis-admin";
import { routes } from "../../routing/routes";
import { getApiErrorMessage } from "../../api/api-error";
import {
  useDefaultConfig,
  usePraxis,
  useSetupPraxisDomain,
  useUpdatePraxisConfig,
  useUpdatePraxisEmail,
} from "./hooks/usePraxes";
import { FieldHint, FormSection } from "./components/form-fields";
import { PageStatus } from "./components/admin-layout";
import { DomainSetupResultView } from "./components/domain-setup-result";
import {
  PraxisConfigFields,
  PraxisConfigFormShape,
} from "./praxis-config-form/praxis-config-fields";
import {
  readFileAsBase64,
  toConfigFormValues,
  toConfigInput,
} from "./praxis-config-form/config-form-values";

const PraxisSection: React.FC<{ praxis: PraxisDetails }> = ({ praxis }) => {
  const { t, i18n } = useTranslation();
  const updateEmail = useUpdatePraxisEmail(praxis.subdomain);
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting, isDirty },
  } = useForm<{ email: string }>({ defaultValues: { email: praxis.email } });

  const onSubmit = async ({ email }: { email: string }) => {
    try {
      await updateEmail.mutateAsync(email.trim());
      reset({ email: email.trim() });
      toast.success(t("admin.praxis.emailSaved"));
    } catch (error) {
      toast.error(getApiErrorMessage(error) ?? t("admin.praxis.saveError"));
    }
  };

  return (
    <FormSection title={t("admin.praxis.title")}>
      <dl className="grid gap-x-6 gap-y-2 text-sm sm:grid-cols-[max-content_1fr]">
        <dt className="font-semibold text-gray-800">
          {t("admin.praxis.login")}
        </dt>
        <dd className="text-gray-700">{praxis.usernames.join(", ") || "–"}</dd>
        <dt className="font-semibold text-gray-800">
          {t("admin.praxis.submittedQuestionnaires")}
        </dt>
        <dd className="text-gray-700">
          {praxis.submittedQuestionnaires.toLocaleString(i18n.language)}
        </dd>
        <dt className="font-semibold text-gray-800">
          {t("admin.praxis.createdAt")}
        </dt>
        <dd className="text-gray-700">
          {new Date(praxis.createdAt).toLocaleString(i18n.language)}
        </dd>
      </dl>
      <form
        onSubmit={handleSubmit(onSubmit)}
        className="flex flex-col gap-2 sm:flex-row sm:items-start"
        noValidate
      >
        <Input
          id="email"
          type="email"
          className="flex-1"
          label={t("admin.praxis.email")}
          error={errors.email?.message}
          {...register("email", { required: t("admin.form.required") })}
        />
        <Button
          type="submit"
          className="sm:mt-8"
          isLoading={isSubmitting}
          disabled={!isDirty}
        >
          {t("admin.save")}
        </Button>
      </form>
      <FieldHint>{t("admin.praxis.emailHint")}</FieldHint>
    </FormSection>
  );
};

const DomainSection: React.FC<{ praxis: PraxisDetails }> = ({ praxis }) => {
  const { t } = useTranslation();
  const setupDomain = useSetupPraxisDomain(praxis.subdomain);

  return (
    <FormSection
      title={t("admin.domainSetup.title")}
      description={t("admin.domainSetup.description")}
    >
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <a
          href={`https://${praxis.domain}`}
          target="_blank"
          rel="noreferrer"
          className="flex w-fit items-center gap-1 text-primary hover:underline"
        >
          {praxis.domain}
          <ArrowTopRightOnSquareIcon className="h-4 w-4" />
        </a>
        <Button
          type="button"
          isLoading={setupDomain.isPending}
          onClick={() =>
            setupDomain
              .mutateAsync()
              .catch((error) =>
                toast.error(
                  getApiErrorMessage(error) ?? t("admin.domainSetup.error"),
                ),
              )
          }
        >
          <ArrowPathIcon className="h-4 w-4" />
          {t("admin.domainSetup.retry")}
        </Button>
      </div>
      {setupDomain.data && <DomainSetupResultView result={setupDomain.data} />}
    </FormSection>
  );
};

const ConfigForm: React.FC<{
  praxis: PraxisDetails;
  defaults: PraxisConfig;
}> = ({ praxis, defaults }) => {
  const { t } = useTranslation();
  const updateConfig = useUpdatePraxisConfig(praxis.subdomain);
  const form = useForm<PraxisConfigFormShape>({
    defaultValues: {
      config: toConfigFormValues(praxis.config, defaults),
      favicon: null,
    },
  });
  const {
    handleSubmit,
    reset,
    formState: { isSubmitting, isDirty },
  } = form;

  const onSubmit = async (values: PraxisConfigFormShape) => {
    const favicon = values.favicon?.[0];

    try {
      await updateConfig.mutateAsync({
        config: toConfigInput(values.config),
        ...(favicon ? { faviconBase64: await readFileAsBase64(favicon) } : {}),
      });
      reset({ config: values.config, favicon: null });
      toast.success(t("admin.praxis.configSaved"));
    } catch (error) {
      toast.error(getApiErrorMessage(error) ?? t("admin.praxis.saveError"));
    }
  };

  return (
    <FormProvider {...form}>
      <form
        onSubmit={handleSubmit(onSubmit)}
        className="flex flex-col gap-4"
        noValidate
      >
        {!praxis.hasConfig && (
          <div className="rounded-lg border border-orange-200 bg-orange-50 p-3 text-sm text-orange-900">
            {t("admin.praxis.noConfig")}
          </div>
        )}
        <PraxisConfigFields
          defaults={defaults}
          subdomain={praxis.subdomain}
          hasFavicon={praxis.hasFavicon}
        />
        <div className="sticky bottom-0 -mx-4 flex justify-end border-t border-gray-200 bg-white/90 px-4 py-3 backdrop-blur md:-mx-6 md:px-6">
          <Button
            type="submit"
            variant="contained"
            isLoading={isSubmitting}
            disabled={!isDirty}
          >
            {t("admin.praxis.saveConfig")}
          </Button>
        </div>
      </form>
    </FormProvider>
  );
};

export const AdminPraxisPage: React.FC = () => {
  const { t } = useTranslation();
  const { subdomain = "" } = useParams();
  const praxis = usePraxis(subdomain);
  const defaults = useDefaultConfig();

  return (
    <>
      <Link
        to={routes.admin}
        className="flex w-fit items-center gap-1 text-sm text-primary hover:underline"
      >
        <ArrowLeftIcon className="h-4 w-4" />
        {t("admin.backToPraxes")}
      </Link>
      <h1 className="text-2xl text-text-primary">{subdomain}</h1>

      {(praxis.isLoading || defaults.isLoading) && (
        <PageStatus>{t("admin.loading")}</PageStatus>
      )}
      {praxis.error && <PageStatus>{t("admin.praxis.loadError")}</PageStatus>}
      {defaults.error && (
        <PageStatus>{t("admin.defaultConfigError")}</PageStatus>
      )}

      {praxis.data && defaults.data && (
        <>
          <PraxisSection praxis={praxis.data} />
          <DomainSection praxis={praxis.data} />
          <h2 className="mt-4 text-xl text-text-primary">
            {t("admin.praxis.configTitle")}
          </h2>
          <ConfigForm
            key={praxis.data.subdomain}
            praxis={praxis.data}
            defaults={defaults.data}
          />
        </>
      )}
    </>
  );
};
